import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetQuotesApiParameter } from "@insite/client-framework/Services/QuoteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        parameter: state.pages.rfqMyQuotes.getQuotesParameter,
        settingsCollection: getSettingsCollection(state),
        session,
        language: state.context.session.language,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    container?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    quoteNumberCell?: DataTableCellProps;
    quoteLink?: LinkPresentationProps;
    typeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    orderDateCell?: DataTableCellProps;
    expirationDateCell?: DataTableCellProps;
    salesRepCell?: DataTableCellProps;
    userCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    customerCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    shipToCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    quoteNumberHeader: {
        css: css`
            position: sticky;
            left: 0;
            width: 150px;
            background-clip: padding-box;
        `,
    },
    quoteNumberCell: {
        css: css`
            position: sticky;
            left: 0;
            width: 150px;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
    sort: "quoteNumber",
};

const styles = recentQuotesStyles;

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    quoteNumber: ["quoteNumber"],
    orderDate: ["orderDate"],
    typeDisplay: ["typeDisplay"],
    statusDisplay: ["statusDisplay"],
    expirationDate: ["expirationDate"],
    salespersonName: ["salespersonName"],
    userName: ["userName"],
    customerName: ["customerName"],
    shipToFullAddress: ["shipToFullAddress"],
};

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
        if (this.props.canViewQuotes) {
            recentQuotesParameter.sort = sort;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    render() {
        const {
            settingsCollection,
            session,
            quotesDataView,
            myQuotesPageNavLink,
            canViewQuotes,
            rfqQuoteDetailsPageUrl,
            language,
        } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        const rows = quotesDataView.value.map(quote => ({
            id: quote.id,
            quoteNumber: quote.quoteNumber,
            type: quote.typeDisplay,
            status: quote.statusDisplay,
            orderDate: quote.orderDate ? getLocalizedDateTime({ dateTime: new Date(quote.orderDate), language }) : "",
            expirationDate: quote.expirationDate
                ? getLocalizedDateTime({ dateTime: new Date(quote.expirationDate), language })
                : "",
            salesRep: quote.salespersonName,
            user: quote.userName,
            customer: `${quote.customerNumber}-${quote.customerName}`,
            shipTo: quote.shipToFullAddress,
        }));

        return (
            <StyledWrapper>
                <CardList
                    css={css`
                        padding-bottom: 50px;
                    `}
                    extendedStyles={styles.cardList}
                >
                    <CardListHeading heading={translate("Recent Quotes")} viewAllUrl={myQuotesPageUrl} />
                    {quotesDataView.value.length === 0 && (
                        <GridItem {...styles.noQuotesGridItem}>
                            <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                        </GridItem>
                    )}
                </CardList>
                {rows.length > 0 && (
                    <StyledWrapper {...styles.container}>
                        <DataTable {...styles.dataTable} data-test-selector="rfqMyQuotes_quoteTable">
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.quoteNumberHeader} title={translate("Quote Number")}>
                                    {translate("Quote #")}
                                </DataTableHeader>
                                {settingsCollection.quoteSettings.jobQuoteEnabled && (
                                    <DataTableHeader tight {...styles.typeHeader}>
                                        {translate("Type")}
                                    </DataTableHeader>
                                )}
                                <DataTableHeader tight {...styles.statusHeader}>
                                    {translate("Status")}
                                </DataTableHeader>
                                <DataTableHeader tight {...styles.orderDateHeader}>
                                    {translate("Requested")}
                                </DataTableHeader>
                                <DataTableHeader tight {...styles.expirationDateHeader}>
                                    {translate("Expires")}
                                </DataTableHeader>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableHeader tight {...styles.salesRepHeader}>
                                            {translate("Sales Rep")}
                                        </DataTableHeader>
                                        <DataTableHeader tight {...styles.userHeader}>
                                            {translate("User")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader tight {...styles.customerHeader}>
                                    {translate("Customer")}
                                </DataTableHeader>
                                {!session.isSalesPerson && (
                                    <DataTableHeader tight {...styles.shipToHeader} title={translate("Fulfillment")}>
                                        {translate("Ship To/Pick Up")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {rows.map(
                                    ({
                                        id,
                                        quoteNumber,
                                        type,
                                        status,
                                        orderDate,
                                        expirationDate,
                                        salesRep,
                                        user,
                                        customer,
                                        shipTo,
                                    }) => (
                                        <DataTableRow
                                            key={id}
                                            {...styles.dataTableRow}
                                            data-test-selector={`rfqMyQuotes_quoteLine_${id}`}
                                        >
                                            <DataTableCell {...styles.quoteNumberCell}>
                                                <Link
                                                    {...styles.quoteLink}
                                                    href={`${rfqQuoteDetailsPageUrl}?quoteId=${id}`}
                                                    data-test-selector="link"
                                                    title={translate("View Quote")}
                                                >
                                                    {quoteNumber}
                                                </Link>
                                            </DataTableCell>
                                            {settingsCollection.quoteSettings.jobQuoteEnabled && (
                                                <DataTableCell {...styles.typeCell}>{type}</DataTableCell>
                                            )}
                                            <DataTableCell {...styles.statusCell}>{status}</DataTableCell>
                                            <DataTableCell {...styles.orderDateCell}>{orderDate}</DataTableCell>
                                            <DataTableCell {...styles.expirationDateCell}>
                                                {expirationDate}
                                            </DataTableCell>
                                            {session.isSalesPerson && (
                                                <>
                                                    <DataTableCell {...styles.salesRepCell}>{salesRep}</DataTableCell>
                                                    <DataTableCell {...styles.userCell} data-test-selector="user">
                                                        {user}
                                                    </DataTableCell>
                                                </>
                                            )}
                                            <DataTableCell {...styles.customerCell}>{customer}</DataTableCell>
                                            {!session.isSalesPerson && (
                                                <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
                                            )}
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
